/* eslint-disable react-native/no-inline-styles */
/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { Alert, Image, Space, Spin, Upload } from 'antd'
import React from 'react'
import { captionLarge } from 'src/components/css'
import banners from '../../Form/banner-helpers'
import { Separator } from '../GeneralForm'
import { useScheduleContext } from '../ScheduleContext'
import '../styles.scss'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import Text from 'src/components/ts-components/Text'

type DaysmartFormProps = {
  onCancelPressed: () => void
  triggerRefresh: () => void
}

const CustomizationForm: React.FC<DaysmartFormProps> = ({
  onCancelPressed,
  triggerRefresh,
}) => {
  const {
    isEdit,
    error,
    setError,
    isLoading,
    onSubmit,
    onCancel,
    backgroundImageUrl,
    setBackgroundImageUrl,
    beforeUpload,
    handleChangeBackground,
  } = useScheduleContext()

  return (
    <div
      css={css`
        padding-top: 16px;
        padding-left: 16px;
        padding-right: 16px;
        display: flex;
        flex: 1;
        flex-direction: column;
        height: 100%;
        overflow-y: auto;
        max-height: 550px;
        gap: 16px;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          gap: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <Text variant='heading5' weight='semiBold' color={colors.NEUTRAL_0}>
              Customization
            </Text>
          </div>
        </div>

        {backgroundImageUrl ? (
          <div
            css={css`
              display: flex;
              padding: 8px;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              border-radius: 8px;
              background-color: ${colors.SECONDARY_100};
              border: 1px solid ${colors.SECONDARY_200};
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                flex: 1 0 0;
              `}
            >
              <Image
                src={backgroundImageUrl}
                alt='background'
                width={160}
                height={48}
                css={css`
                  border-radius: 4px;
                  object-fit: cover;
                `}
                preview={false}
              />
              <DeleteOutlined
                css={css`
                  color: ${colors.ERROR_200};
                  font-size: 24px;
                  cursor: pointer;
                `}
                onClick={() => {
                  setBackgroundImageUrl(null)
                }}
              />
            </div>
          </div>
        ) : (
          <Upload
            beforeUpload={beforeUpload}
            onChange={handleChangeBackground}
            showUploadList={false}
            action={undefined}
          >
            <div
              css={css`
                display: flex;
                padding: 8px;
                align-items: center;
                gap: 8px;
                align-self: stretch;
                border-radius: 8px;
                border: 1px dashed ${colors.SECONDARY_200};
                cursor: pointer;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  flex: 1 0 0;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    width: 48px;
                    height: 48px;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    border-radius: 4px;
                    background: ${colors.PRIMARY};
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      width: 24px;
                      height: 24px;
                      flex-direction: column;
                      justify-content: center;
                      align-items: center;
                      border-radius: 4px;
                      background: ${colors.WHITE};
                    `}
                  >
                    {/* antd plus icon */}
                    <PlusOutlined
                      css={css`
                        font-size: 24px;
                        color: ${colors.HEADER};
                        background-color: ${colors.PRIMARY};
                      `}
                    />
                  </div>
                </div>
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 4px;
                    `}
                  >
                    <Text
                      variant='paragraphSmall'
                      weight='regular'
                      color={colors.NEUTRAL_0}
                    >
                      Custom Cover
                    </Text>
                    <Text
                      variant='paragraphXSmall'
                      weight='medium'
                      color={colors.NEUTRAL_100}
                    >
                      (Recommended: 1440x300)
                    </Text>
                  </div>
                  <Text
                    variant='paragraphSmall'
                    weight='medium'
                    color={colors.PRIMARY}
                  >
                    Click to Upload
                  </Text>
                </div>
              </div>
            </div>
          </Upload>
        )}
        <Separator />
      </div>
      <div
        css={css`
          position: fixed;
          bottom: 20px;
          right: 38px;
          width: 200px;
          background-color: ${colors.HEADER};
          z-index: 100;
          padding-bottom: 16px;
          padding-top: 16px;
        `}
      >
        {error ? (
          <Space
            direction='vertical'
            style={{ width: '100%', marginBottom: 8 }}
          >
            <Alert
              message={error || 'An error occurred. Please try again.'}
              type='error'
              showIcon
              closable
            />
          </Space>
        ) : null}
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 16px;
            align-self: stretch;
          `}
        >
          {/* Cancel Button */}
          <div
            css={css`
              max-width: 80px;
              display: flex;
              padding: 8px 24px;
              justify-content: center;
              align-items: center;
              gap: 8px;
              flex: 1 0 0;
              border-radius: 8px;
              border: 1px solid var(--Neutrals-neutrals-0, #fff);
              cursor: ${isLoading ? 'not-allowed' : 'pointer'};
              opacity: ${isLoading ? 0.7 : 1};
            `}
            onClick={() => {
              if (isLoading) {
                return
              }
              // Clear the state
              onCancel()
              //   and close the modal
              onCancelPressed()
            }}
          >
            <p
              css={css`
                ${captionLarge}
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                color: ${colors.WHITE};
              `}
            >
              Cancel
            </p>
          </div>
          <div
            css={css`
              max-width: 80px;
              display: flex;
              padding: 8px 24px;
              justify-content: center;
              align-items: center;
              gap: 8px;
              flex: 1 0 0;
              border-radius: 8px;
              background: ${colors.PRIMARY};
              cursor: ${isLoading ? 'not-allowed' : 'pointer'};
              opacity: ${isLoading ? 0.7 : 1};
            `}
            onClick={async () => {
              try {
                if (isLoading) {
                  return
                }
                const response = await onSubmit()
                if (isEdit) {
                  // @ts-ignore
                  banners.entityUpdate(ENTITY_TYPES.schedule)(response)
                } else {
                  banners.entityCreate(ENTITY_TYPES.schedule)(response)
                }
                triggerRefresh()
                onCancelPressed()
                onCancel()
              } catch (e: any) {
                const errorMessage = getErrorMessage(e)
                if (e?.invalid_fields) {
                  if (
                    e?.invalid_fields?.name ||
                    e?.invalid_fields?.abbreviation
                  ) {
                    setError(errorMessage)
                  }
                }
              }
            }}
          >
            <>
              {isLoading ? (
                <Spin
                  size='small'
                  css={css`
                    color: ${colors.WHITE};
                  `}
                />
              ) : (
                <p
                  css={css`
                    ${captionLarge}
                    font-family: ${isCanlan
                      ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                      : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                    color: ${colors.WHITE};
                  `}
                >
                  Save
                </p>
              )}
            </>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomizationForm
